import { Tooltip, TooltipCard, TooltipTrigger } from "design-system/atoms/Tooltip"
import { cn } from "design-system/utils"
import { formatNumber } from "lopermut"
import { ArrowUpRight, Verified } from "lucide-react"
import { useTranslation } from "react-i18next"
import { CustomerResponseDataSchema } from "schema"
import z from "zod"

type Props = {
  className?: string
  profile: NonNullable<z.infer<typeof CustomerResponseDataSchema>["instagramProfile"]>
}

export const InstagramProfilePopover = ({ profile, className }: Props) => {
  const { t } = useTranslation()

  if (!profile.username) return null

  const profileLabel = `@${profile.username}`

  return (
    <Tooltip delayDuration={250}>
      <TooltipTrigger className="flex flex-col gap-2" asChild>
        <span
          className={cn(
            "text-sm underline decoration-dashed decoration-[0.5px] underline-offset-2 decoration-muted-foreground",
            className,
          )}
        >
          {profileLabel}
        </span>
      </TooltipTrigger>
      <TooltipCard className="p-4">
        <div className="grid lg:grid-cols-4 grid-cols-1 gap-4">
          <div className="col-span-1">
            <img
              src={profile.profilePicture ?? "/insta-no-profile.png"}
              className="w-16 rounded-full"
            />
          </div>
          <div className="col-span-1 lg:col-span-3 flex flex-col">
            <span className="flex gap-1 items-center font-medium">
              {profile.name ?? profileLabel}
              {profile.isVerified && (
                <Verified size={18} className="text-white" fill={"rgb(0, 149, 246)"} />
              )}
            </span>
            <a
              href={`https://instagram.com/${profile.username}`}
              target="_blank"
              className="flex gap-1 underline underline-offset-2 items-center text-xs text-muted-foreground"
              rel="noreferrer noopener"
            >
              <span>{profileLabel}</span>
              <ArrowUpRight size={12} />
            </a>
            <div className="flex gap-1 text-xs justify-between mt-2">
              <span>{t("customers.profile.instagramFollowers")}</span>
              <span className="font-medium">
                {formatNumber({ amount: profile.followersCount ?? 0 })}
              </span>
            </div>
          </div>
        </div>
      </TooltipCard>
    </Tooltip>
  )
}
